import { useNavigate } from 'react-router-dom';
import { authService } from '../services/auth-service';

interface UnauthenticatedErrorProps {
  error?: string;
  details?: string;
}

/**
 * Component to show when user is unauthenticated
 * Shows a message and button to return to login instead of auto-redirecting
 */
export function UnauthenticatedError({ error, details }: UnauthenticatedErrorProps) {
  const navigate = useNavigate();

  const handleReturnToLogin = () => {
    localStorage.removeItem('tn_auth');
    localStorage.removeItem('tn_pkce_verifier');
    localStorage.removeItem('tn_processed_code');
    // If a token exchange is currently processing, avoid interrupting it.
    if ((authService as any).isProcessing && (authService as any).isProcessing()) {
      // Keep the user on the page; the AuthCallback flow will finish shortly.
      return;
    }

    authService.login();
    navigate('/login', { replace: true });
  };

  return (
    <div className="min-h-screen flex items-center justify-center bg-gray-50">
      <div className="max-w-md w-full space-y-8">
        <div className="text-center">
          <div className="mx-auto h-12 w-12 text-red-500">
            <svg className="h-12 w-12" fill="none" viewBox="0 0 24 24" stroke="currentColor">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-2.5L13.732 4c-.77-.833-1.732-.833-2.5 0L4.268 18.5c-.77.833.192 2.5 1.732 2.5z" />
            </svg>
          </div>
          <h2 className="mt-6 text-3xl font-bold tracking-tight text-gray-900">
            Authentication Required
          </h2>
          <p className="mt-2 text-sm text-gray-600">
            You are not authenticated or your session has expired.
          </p>
          
          {error && (
            <div className="mt-4 p-4 bg-red-50 border border-red-200 rounded-md">
              <p className="text-sm text-red-800">
                <strong>Error:</strong> {error}
              </p>
              {details && (
                <p className="text-xs text-red-600 mt-2">
                  {details}
                </p>
              )}
            </div>
          )}
          
          <div className="mt-6">
            <button
              onClick={handleReturnToLogin}
              disabled={(authService as any).isProcessing && (authService as any).isProcessing()}
              className="group relative w-full flex justify-center py-2 px-4 border border-transparent text-sm font-medium rounded-md text-white bg-blue-600 hover:bg-blue-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500 transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
            >
              Return to Login
            </button>
          </div>
          
          <div className="mt-4 text-xs text-gray-500">
            <p>
              If you continue to experience issues, please contact support.
            </p>
          </div>
        </div>
      </div>
    </div>
  );
}
